import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { getProxy, updateProxy } from '../../service/request/proxyRequest';

const ProxySettings = () => {
    const [proxyData, setProxyData] = useState({
        ip_address: '',
        port: '',
        username: '',
        password: ''
    });

    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const toast = useRef(null);

    useEffect(() => {
        fetchProxyData();
    }, []);

    const fetchProxyData = async () => {
        try {
            setInitialLoading(true);
            const response = await getProxy();
            if (response.data) {
                setProxyData(response.data.data);
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Hata',
                detail: 'Proxy ayarları yüklenirken bir hata oluştu',
                life: 3000
            });
        } finally {
            setInitialLoading(false);
        }
    };

    const onInputChange = (e, fieldName) => {
        const value = e.target.value;
        setProxyData((prev) => ({
            ...prev,
            [fieldName]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        // Validation
        if (!proxyData.ip_address?.trim() || !proxyData.port?.toString().trim() || !proxyData.username?.trim() || !proxyData.password?.trim()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Uyarı',
                detail: 'Tüm alanları doldurmanız gerekmektedir',
                life: 3000
            });
            return;
        }

        // Port validation
        const portNumber = parseInt(proxyData.port);
        if (isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
            toast.current.show({
                severity: 'warn',
                summary: 'Uyarı',
                detail: 'Geçerli bir port numarası giriniz (1-65535)',
                life: 3000
            });
            return;
        }

        try {
            setLoading(true);

            // Remove unwanted fields from proxyData before sending
            const { created_at, updated_at, ...cleanProxyData } = proxyData;

            await updateProxy({
                ...cleanProxyData,
                port: portNumber
            });

            toast.current.show({
                severity: 'success',
                summary: 'Başarılı',
                detail: 'Proxy ayarları başarıyla güncellendi',
                life: 3000
            });

            setSubmitted(false);
        } catch (error) {
            console.log(error);
            toast.current.show({
                severity: 'error',
                summary: 'Hata',
                detail: 'Proxy ayarları güncellenirken bir hata oluştu',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
            </div>
        );
    }

    const isFormValid = () => {
        return proxyData.ip_address?.trim() && proxyData.port?.toString().trim() && proxyData.username?.trim() && proxyData.password?.trim() && !isNaN(parseInt(proxyData.port)) && parseInt(proxyData.port) > 0 && parseInt(proxyData.port) <= 65535;
    };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12">
                <div className="card">
                    <h5>Proxy Ayarları</h5>
                    <p>Sistemin kullanacağı proxy sunucu ayarlarını buradan yapabilirsiniz.</p>

                    <Card>
                        <form onSubmit={handleSubmit} className="p-fluid">
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="ip_address" className="font-bold">
                                            IP Adresi <span className="text-pink-500">*</span>
                                        </label>
                                        <InputText
                                            id="ip_address"
                                            value={proxyData.ip_address}
                                            onChange={(e) => onInputChange(e, 'ip_address')}
                                            placeholder="IP Adresi"
                                            className={classNames({
                                                'p-invalid': submitted && !proxyData.ip_address?.trim()
                                            })}
                                        />
                                        {submitted && !proxyData.ip_address?.trim() && <small className="p-error">IP adresi gereklidir.</small>}
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="port" className="font-bold">
                                            Port <span className="text-pink-500">*</span>
                                        </label>
                                        <InputText
                                            id="port"
                                            value={proxyData.port}
                                            onChange={(e) => onInputChange(e, 'port')}
                                            placeholder="Port"
                                            className={classNames({
                                                'p-invalid': submitted && (!proxyData.port?.toString().trim() || isNaN(parseInt(proxyData.port)) || parseInt(proxyData.port) < 1 || parseInt(proxyData.port) > 65535)
                                            })}
                                        />
                                        {submitted && !proxyData.port?.toString().trim() && <small className="p-error">Port numarası gereklidir.</small>}
                                        {submitted && proxyData.port?.toString().trim() && (isNaN(parseInt(proxyData.port)) || parseInt(proxyData.port) < 1 || parseInt(proxyData.port) > 65535) && (
                                            <small className="p-error">Geçerli bir port numarası giriniz (1-65535).</small>
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="username" className="font-bold">
                                            Kullanıcı Adı <span className="text-pink-500">*</span>
                                        </label>
                                        <InputText
                                            id="username"
                                            value={proxyData.username}
                                            onChange={(e) => onInputChange(e, 'username')}
                                            placeholder="Kullanıcı Adı"
                                            className={classNames({
                                                'p-invalid': submitted && !proxyData.username?.trim()
                                            })}
                                        />
                                        {submitted && !proxyData.username?.trim() && <small className="p-error">Kullanıcı adı gereklidir.</small>}
                                    </div>
                                </div>

                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="password" className="font-bold">
                                            Şifre <span className="text-pink-500">*</span>
                                        </label>
                                        <InputText
                                            id="password"
                                            type="password"
                                            value={proxyData.password}
                                            onChange={(e) => onInputChange(e, 'password')}
                                            placeholder="Proxy şifresi"
                                            className={classNames({
                                                'p-invalid': submitted && !proxyData.password?.trim()
                                            })}
                                        />
                                        {submitted && !proxyData.password?.trim() && <small className="p-error">Şifre gereklidir.</small>}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-content-end gap-2 mt-3">
                                <Button type="button" label="Yenile" icon="pi pi-refresh" outlined onClick={fetchProxyData} disabled={loading} />
                                <Button type="submit" label="Kaydet" icon="pi pi-save" loading={loading} disabled={!isFormValid()} />
                            </div>
                        </form>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ProxySettings;
